import React from 'react';
import {useSelector} from 'react-redux';
import {useActiveYear} from 'src/common/hooks/useActiveYear';
import {getCountyNameFromCountyCode} from 'src/common/utils';
import {TaxProfileSubsection} from 'src/constants/constants';
import {TaxProfileField} from 'src/store/taxProfile/taxProfile.types';
import {TaxProfileQuestion} from 'src/store/taxProfile/taxProfile.types';
import MultiKeyValueField from 'src/CpaCenterV2/TaxProfileSections/common/MultiKeyValueField';
import SimpleKeyValueField from 'src/CpaCenterV2/TaxProfileSections/common/SimpleKeyValueField';
import SubSectionBox from 'src/CpaCenterV2/TaxProfileSections/common/SubSectionBox';
import {isSchoolDistrictCodeRequired} from 'src/CpaCenterV2/TaxProfileSections/TaxProfileSections.utils';
import {ReduxStateType} from 'src/store/store';
import {
  selectTaxProfile,
  selectTaxProfileAnswer,
} from 'src/store/taxProfile/taxProfile.selector';

interface HomeAddressProps {
  style?: React.CSSProperties;
  onEdit?: (subsection: TaxProfileSubsection) => void;
}

const HomeAddress = ({style, onEdit}: HomeAddressProps) => {
  const {loaded} = useSelector(selectTaxProfile);

  const {activeYear} = useActiveYear();

  const homeAddress = useSelector((state: ReduxStateType) =>
    selectTaxProfileAnswer(state, TaxProfileQuestion.HOME_ADDRESS, activeYear),
  );

  const livingOutside = useSelector((state: ReduxStateType) =>
    selectTaxProfileAnswer(
      state,
      TaxProfileQuestion.LIVING_OUTSIDE_USA,
      activeYear,
    ),
  );

  const outsideAddress = useSelector((state: ReduxStateType) =>
    selectTaxProfileAnswer(
      state,
      TaxProfileQuestion.MAILING_ADDRESS_OUTSIDE_USA,
      activeYear,
    ),
  );

  const getFields = () => {
    const fields = [];
    if (!loaded) {
      return fields;
    }

    fields.push(
      ...[
        {name: 'House number and street', path: TaxProfileField.STREET_NUMBER},
        {
          name: 'Apartment/Unit number (if applicable)',
          path: TaxProfileField.APARTMENT_NUMBER,
        },
        {name: 'City', path: TaxProfileField.CITY},
        {name: 'State', path: TaxProfileField.STATE},
        {
          name: 'County',
          path: TaxProfileField.COUNTY,
          parseReadValue: getCountyNameFromCountyCode,
        },
        {name: 'Zip code', path: TaxProfileField.ZIP_CODE},
      ],
    );

    if (
      isSchoolDistrictCodeRequired(homeAddress?.[TaxProfileField.STATE]?.[0])
    ) {
      fields.push({
        name: 'School district code',
        path: TaxProfileField.SCHOOL_DISTRICT_CODE,
      });
    }

    return fields;
  };

  return (
    <SubSectionBox
      name="Home address"
      style={style}
      onEdit={onEdit}
      taxprofileSubsection={TaxProfileSubsection.HomeAddress}>
      <SimpleKeyValueField
        name="I currently living outside USA"
        value={livingOutside}
        style={{marginBottom: 12}}
        loading={!loaded}
      />
      {livingOutside ? (
        <SimpleKeyValueField
          name="Mailing address outside USA"
          value={outsideAddress}
        />
      ) : (
        <MultiKeyValueField
          data={homeAddress}
          fields={getFields()}
          loading={!loaded}
        />
      )}
    </SubSectionBox>
  );
};

export default HomeAddress;
